import React, { useState } from 'react';
import imageCompression from 'browser-image-compression';

const UpdateWebinarInstructor = ({ formData, handleSubmit, handleChange, currentImage, onCancel }) => {
  const [isCompressing, setIsCompressing] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsCompressing(true);
    try {
      // Compress image
      const options = {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 1024,
        useWebWorker: true,
        fileType: 'image/webp'
      };
      const compressedFile = await imageCompression(file, options);

      // Create preview
      const previewUrl = URL.createObjectURL(compressedFile);
      setImagePreview(previewUrl);

      // Update form data
      handleChange({
        target: {
          name: 'image',
          files: [compressedFile]
        }
      });
    } catch (error) {
      console.error('Error compressing image:', error);
    } finally {
      setIsCompressing(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Update Instructor</h2>
        <p className="text-sm text-gray-500">Edit the instructor details below</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {/* Slug Field */}
          <div className="sm:col-span-2">
            <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1">
              Unique Identifier
            </label>
            <input
              type="text"
              id="slug"
              name="slug"
              value={formData.slug}
              onChange={handleChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
              placeholder="e.g., john-doe"
              required
            />
          </div>

          {/* Image Field */}
          <div className="sm:col-span-2">
            <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-1">
              Profile Image
            </label>
            <div className="flex items-center space-x-4">
              {(imagePreview || currentImage) && (
                <div className="relative">
                  <img 
                    src={imagePreview || currentImage} 
                    alt="Preview" 
                    className="h-24 w-24 rounded-full object-cover border-2 border-gray-200" 
                  />
                  <p className="text-xs text-gray-500 mt-1 text-center">
                    {imagePreview ? 'New image' : 'Current image'}
                  </p>
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center justify-center w-full">
                  <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer ${isCompressing ? 'bg-gray-100' : 'bg-gray-50 hover:bg-gray-100'} border-gray-300`}>
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      {isCompressing ? (
                        <div className="flex flex-col items-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-2"></div>
                          <p className="text-sm text-gray-500">Compressing...</p>
                        </div>
                      ) : (
                        <>
                          <svg className="w-8 h-8 mb-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                          </svg>
                          <p className="mb-2 text-sm text-gray-500">
                            <span className="font-semibold">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-gray-500">WEBP, PNG, JPG (MAX. 2MB)</p>
                        </>
                      )}
                    </div>
                    <input 
                      id="image" 
                      name="image" 
                      type="file" 
                      onChange={handleImageChange}
                      accept="image/*"
                      className="hidden"
                      disabled={isCompressing}
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Headline Field */}
          <div className="sm:col-span-2">
            <label htmlFor="headline" className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              type="text"
              id="headline"
              name="headline"
              value={formData.headline}
              onChange={handleChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
              placeholder="e.g., Danial islam"
              required
            />
          </div>

          {/* Experience Field */}
          <div className="sm:col-span-2">
            <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-1">
              Professional Experience
            </label>
            <textarea
              id="experience"
              name="experience"
              value={formData.experience}
              onChange={handleChange}
              rows="4"
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
              placeholder="Describe the instructor's experience and qualifications..."
              required
            ></textarea>
          </div>

          {/* Goal Field */}
          <div className="sm:col-span-2">
            <label htmlFor="goal" className="block text-sm font-medium text-gray-700 mb-1">
              Teaching Philosophy
            </label>
            <textarea
              id="goal"
              name="goal"
              value={formData.goal}
              onChange={handleChange}
              rows="4"
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
              placeholder="What are the instructor's teaching goals and approach?"
              required
            ></textarea>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            disabled={isCompressing}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            disabled={isCompressing}
          >
            {isCompressing ? 'Processing...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UpdateWebinarInstructor;